// src/components/mdx/client/visualizations/CpaAxisComparisonChart.client.tsx

/**
 * Side-by-side CPA bar charts demonstrating truncated vs zero-based Y-axes.
 */

'use client'

import { useMemo } from 'react'

import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from '~/lib/charts/client'
import type { NameType, TooltipContentType, ValueType } from '~/lib/charts/types'
import { getOrCreateTooltipWrapper, type CustomTooltipProps } from '~/lib/charts/tooltip'
import { AXIS_TOKENS, MARKETING_DATA_VIZ_SERIES } from '~/components/mdx/client/visualizations/blogChartPalette'
import { BlogChartShell } from '~/components/mdx/client/visualizations/BlogChartShell.client'
import { BLOG_CHART_THROTTLE_MS, BlogChartPlotArea, BlogChartTooltipShell, BLOG_CHART_LABEL_STYLE, formatBarEndCurrency } from '~/components/mdx/client/visualizations/blogChartLayout'
import { ChartVisibilityBoundary } from '~/components/mdx/client/visualizations/ChartVisibilityBoundary.client'
import { generateCpaAxisComparisonData } from '~/components/mdx/client/visualizations/marketingDataVizData'
import { useMediaQuery } from '~/lib/hooks/useMediaQuery'
import { cn } from '~/lib/utils'

function CpaTooltip({ active, payload, label }: CustomTooltipProps): JSX.Element | null {
  if (!active || !payload?.length) {
    return null
  }
  const value = typeof payload[0]?.value === 'number' ? payload[0].value : payload[0]?.value
  return (
    <BlogChartTooltipShell title={label !== undefined ? String(label) : undefined}>
      <p className="text-gray-300">
        CPA: <span className="font-semibold text-white">${value}</span>
      </p>
    </BlogChartTooltipShell>
  )
}

interface MiniAxisChartProps {
  data: ReturnType<typeof generateCpaAxisComparisonData>
  domain: [number, number]
  accent: string
  isActive: boolean
  prefersReducedMotion: boolean
}

function MiniAxisChart({ data, domain, accent, isActive, prefersReducedMotion }: MiniAxisChartProps): JSX.Element {
  const tooltip = useMemo(() => getOrCreateTooltipWrapper(CpaTooltip), [])

  return (
    <ResponsiveContainer width="100%" height="100%" debounce={50} initialDimension={{ width: 320, height: 260 }}>
      <BarChart data={data} margin={{ top: 20, right: 8, left: 0, bottom: 8 }} accessibilityLayer throttleDelay={BLOG_CHART_THROTTLE_MS}>
        <CartesianGrid stroke={AXIS_TOKENS.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="campaign" stroke={AXIS_TOKENS.stroke} fontSize={10} interval={0} />
        <YAxis stroke={AXIS_TOKENS.stroke} fontSize={10} domain={domain} tickFormatter={value => `$${value}`} />
        <Tooltip content={tooltip as TooltipContentType<ValueType, NameType>} />
        <Bar dataKey="cpa" name="CPA" radius={[4, 4, 0, 0]} animationDuration={isActive && !prefersReducedMotion ? 500 : 0}>
          <LabelList dataKey="cpa" position="top" formatter={formatBarEndCurrency} {...BLOG_CHART_LABEL_STYLE} />
          {data.map(entry => (
            <Cell key={entry.campaign} fill={accent} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

interface CpaAxisComparisonChartContentProps {
  isActive: boolean
}

function CpaAxisComparisonChartContent({ isActive }: CpaAxisComparisonChartContentProps): JSX.Element {
  const data = useMemo(() => generateCpaAxisComparisonData(), [])
  const isMobile = useMediaQuery('(max-width: 768px)')
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const minCpa = Math.min(...data.map(row => row.cpa))
  const maxCpa = Math.max(...data.map(row => row.cpa))

  return (
    <BlogChartShell
      title="Axis Integrity: Small Changes, Big Impact"
      description="The same three CPA values — honest scale vs truncated axis."
      gradient="from-violet-400 to-fuchsia-400"
    >
      <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
        <div className="rounded-2xl border border-rose-400/25 bg-rose-400/5 p-4">
          <div className="mb-3 flex items-start gap-2">
            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-500/20 text-xs font-bold text-rose-300" aria-hidden="true">
              ✕
            </span>
            <div>
              <p className="text-sm font-semibold text-rose-200">Truncated Y-axis</p>
              <p className="text-xs text-rose-300/80">Misleading — Y-axis truncated near ${minCpa}, not $0</p>
            </div>
          </div>
          <BlogChartPlotArea className="h-[240px] sm:h-[260px]">
            <MiniAxisChart
              data={data}
              domain={[minCpa - 2, maxCpa + 2]}
              accent={MARKETING_DATA_VIZ_SERIES.accent}
              isActive={isActive}
              prefersReducedMotion={prefersReducedMotion}
            />
          </BlogChartPlotArea>
        </div>

        <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/5 p-4">
          <div className="mb-3 flex items-start gap-2">
            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-xs font-bold text-emerald-300" aria-hidden="true">
              ✓
            </span>
            <div>
              <p className="text-sm font-semibold text-emerald-200">Zero-based Y-axis</p>
              <p className="text-xs text-emerald-300/80">Accurate — $48→$52 is a modest shift</p>
            </div>
          </div>
          <BlogChartPlotArea className="h-[240px] sm:h-[260px]">
            <MiniAxisChart
              data={data}
              domain={[0, 60]}
              accent={MARKETING_DATA_VIZ_SERIES.mer}
              isActive={isActive}
              prefersReducedMotion={prefersReducedMotion}
            />
          </BlogChartPlotArea>
        </div>
      </div>
    </BlogChartShell>
  )
}

export default function CpaAxisComparisonChart(): JSX.Element {
  return (
    <ChartVisibilityBoundary aria-label="CPA axis integrity truncated versus zero-based comparison">
      {({ isIntersecting }) => (
        <CpaAxisComparisonChartContent isActive={isIntersecting} />
      )}
    </ChartVisibilityBoundary>
  )
}
